import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
} from '@chakra-ui/react';
import { IconEyeOpen } from 'central-icons/IconEyeOpen';
import { IconEyeSlash } from 'central-icons/IconEyeSlash';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { ACCOUNT_PORTAL_ROOT, login, RouterRoutePaths } from '@revelio/auth';
import { Button, Callout, Input, Text, useDisclosure } from '@revelio/ui';

import { useBrand } from '../../shared/branding/brand-provider';
import styles from './login.module.css';

const isLocalHost = window.location.hostname === 'localhost';

const Login = () => {
  const brand = useBrand();
  const BrandFullLogo = brand.logos.fullLogo;
  const defaultValues = {
    username: '',
    password: '',
  };
  const navigate = useNavigate();

  useEffect(() => {
    if (window.location.hostname !== 'localhost') {
      window.location.assign(`${ACCOUNT_PORTAL_ROOT}/login`);
    }
  }, []);
  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
    // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
  } = useForm<any>({ defaultValues });

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const onSubmit = ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }) => {
    return login({ username, password })
      .then(({ userId }: { userId?: string }) => {
        if (userId) {
          reset();

          if (isLocalHost) {
            navigate(RouterRoutePaths.LANDING);
          }
        }
      })
      .catch((err: Error) => {
        console.log(err);
        setErrorMsg('Wrong username or password.');
      });
  };

  const { isOpen, onToggle } = useDisclosure();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const onClickReveal = () => {
    onToggle();
    const input = inputRef.current;
    if (input) {
      input.focus({ preventScroll: true });
      const length = input.value.length * 2;
      requestAnimationFrame(() => {
        input.setSelectionRange(length, length);
      });
    }
  };

  return (
    <div className={styles.page}>
      <div>
        <div className={styles.header}>
          <BrandFullLogo mx="auto" h="39" mb={{ base: '4', md: '6' }} />
          <Text as="h2" fontSize="lg" weight="bold" className={styles.title}>
            Please log in
          </Text>
          <div className={styles.formCard}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className={styles.form}>
                {errorMsg && <Callout variant="error">{errorMsg}</Callout>}
                <FormControl id="username" isInvalid={!!errors.username}>
                  <FormLabel color="text.primary" fontWeight="semibold">
                    Username
                  </FormLabel>
                  {/** biome-ignore lint/a11y/noPositiveTabindex: migrated from develop */}
                  <Input
                    id="username"
                    isInvalid={!!errors.username}
                    aria-invalid={!!errors.username || undefined}
                    aria-describedby={
                      errors.username ? 'username-feedback' : undefined
                    }
                    wrapperClassName={styles.filledInput}
                    type="text"
                    autoFocus
                    placeholder="Enter your username"
                    tabIndex={1}
                    {...register('username', {
                      required: 'Username is required.',
                      minLength: {
                        value: 1,
                        message: 'Minimum length should be 1',
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {typeof errors.username?.message === 'string'
                      ? errors.username.message
                      : undefined}
                  </FormErrorMessage>
                </FormControl>
                <FormControl id="password" isInvalid={!!errors.password}>
                  <div className={styles.passwordLabelRow}>
                    <FormLabel color="text.primary" fontWeight="semibold">
                      Password
                    </FormLabel>
                  </div>
                  {/** biome-ignore lint/a11y/noPositiveTabindex: migrated from develop */}
                  <Input
                    id="password"
                    isInvalid={!!errors.password}
                    aria-invalid={!!errors.password || undefined}
                    aria-describedby={
                      errors.password ? 'password-feedback' : undefined
                    }
                    wrapperClassName={styles.filledInput}
                    type={isOpen ? 'text' : 'password'}
                    autoComplete="current-password"
                    tabIndex={2}
                    placeholder="Enter your password"
                    endSlot={
                      <IconButton
                        size="sm"
                        bg="transparent !important"
                        variant="ghost"
                        aria-label={
                          isOpen ? 'Mask password' : 'Reveal password'
                        }
                        icon={
                          isOpen ? (
                            <IconEyeSlash size={16} />
                          ) : (
                            <IconEyeOpen size={16} />
                          )
                        }
                        onClick={onClickReveal}
                        colorScheme="navyBlue"
                        opacity={0.5}
                      />
                    }
                    {...register('password', {
                      required: 'Password is required.',
                      minLength: {
                        value: 4,
                        message: 'Minimum length should be 4',
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {typeof errors.password?.message === 'string'
                      ? errors.password.message
                      : undefined}
                  </FormErrorMessage>
                </FormControl>

                {/** biome-ignore lint/a11y/noPositiveTabindex: migrated from develop */}
                <Button
                  type="submit"
                  color="secondary"
                  size="lg"
                  className={styles.submitButton}
                  tabIndex={3}
                  loading={isSubmitting}
                >
                  Log In
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
